import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import clubPlaceholder from '@sorare/core/src/assets/club/shield_none.png';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  DisplayS,
  HeadlineM,
  HeadlineXL,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { BaseLayout } from 'components/rivals/Onboarding/FavoriteClubSelection/ClubSelectionDialog/BaseLayout';

import { LearnTheBasics_footballRivalsGame } from './__generated__/index.graphql';

const LeftAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;
const CompetitionNameWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--double-unit);
  padding: var(--half-unit) var(--unit);
  background: rgba(var(--c-rgb-white), 0.17);
`;
const TeamImage = styled.img`
  object-fit: contain;
  max-width: 100px;
  max-height: 100px;
`;
const Badge = styled(Horizontal)`
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
`;
const LastGameWraper = styled(Badge)`
  background-color: var(--c-white);
  color: var(--c-black);
`;
const FT = styled(Badge)`
  background-color: rgba(var(--c-rgb-white), 0.07);
  color: var(--c-white);
`;

type Props = {
  color?: string | null;
  rivalsGame?: LearnTheBasics_footballRivalsGame | null;
};
export const LearnTheBasics = ({ rivalsGame, color }: Props) => {
  const { homeGoals, awayGoals, homeTeam, awayTeam, competition, date } =
    rivalsGame?.game || {};

  return (
    <BaseLayout
      color={color}
      title={
        <FormattedMessage
          id="LearnTheBasics.content.title"
          defaultMessage="Replay last match"
        />
      }
      subtitle={
        <FormattedMessage
          id="LearnTheBasics.content.subtitle"
          defaultMessage="Let’s learn the basics"
        />
      }
      content={
        <Horizontal center gap={2}>
          <TeamImage src={homeTeam?.pictureUrl || clubPlaceholder} alt="" />
          <Vertical center>
            <LastGameWraper>
              <LabelS bold>
                <FormattedMessage
                  id="LearnTheBasics.content.lastMatch"
                  defaultMessage="Last match"
                />
              </LabelS>
            </LastGameWraper>
            <HeadlineM>
              {homeGoals} - {awayGoals}
            </HeadlineM>
            <FT>
              <LabelS bold>
                <FormattedMessage {...fantasy.fullTime} />
              </LabelS>
            </FT>
          </Vertical>
          <TeamImage src={awayTeam?.pictureUrl || clubPlaceholder} alt="" />
        </Horizontal>
      }
      footer={
        <LeftAlignedVertical gap={2}>
          {competition?.name && (
            <CompetitionNameWrapper>
              <LabelS color="var(--c-white)">{competition.name}</LabelS>
            </CompetitionNameWrapper>
          )}
          <LeftAlignedVertical gap={0}>
            <DisplayS className="text-center">
              {homeTeam?.shortName} <FormattedMessage {...fantasy.vs} />
            </DisplayS>
            <HeadlineXL className="text-center">
              {awayTeam?.shortName}
            </HeadlineXL>
          </LeftAlignedVertical>
          <LabelL color="var(--c-nd-600)">
            <FormattedDate
              value={date}
              day="numeric"
              month="short"
              year="numeric"
            />
          </LabelL>
        </LeftAlignedVertical>
      }
    />
  );
};

LearnTheBasics.fragments = {
  footballRivalsGame: gql`
    fragment LearnTheBasics_footballRivalsGame on FootballRivalsGame {
      slug
      id
      game {
        id
        date
        homeGoals
        awayGoals
        competition {
          slug
          name
        }
        homeTeam {
          slug
          shortName
          pictureUrl
        }
        awayTeam {
          slug
          shortName
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<LearnTheBasics_footballRivalsGame>,
};
